import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, ExternalLink, Loader2, ShieldAlert, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { KbStyleInjector } from "@/lib/kbSettings";
import { toast } from "sonner";

/** Risk review: walk the flagged Warning boxes ten at a time and decide each
 *  one. "Keep as Warning" is remembered, so nothing is asked twice. */
export default function KBRiskReview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/kb/callouts/risk-review?batch=10");
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load the review");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (row, decision) => {
    setBusy(row.id);
    try {
      if (decision === "keep") {
        await api.post("/kb/callouts/keep", { slug: row.slug, index: row.index });
      } else {
        await api.post("/kb/callouts/promote", {
          slug: row.slug, index: row.index, variant: "danger",
          modernise: decision === "modernise",
        });
      }
      // Drop the card immediately; the batch refills when it empties.
      setData((d) => {
        const batch = d.batch.filter((b) => b.id !== row.id);
        return {
          ...d, batch,
          pending: Math.max(0, d.pending - 1),
          reviewed: d.reviewed + 1,
          promoted: decision === "keep" ? d.promoted : d.promoted + 1,
          kept: decision === "keep" ? d.kept + 1 : d.kept,
        };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record that");
    } finally { setBusy(""); }
  };

  useEffect(() => {
    if (data && !data.batch.length && data.pending > 0 && !loading) load();
  }, [data, loading, load]);

  const done = data && data.pending === 0;
  const pct = data && data.total ? Math.round((data.reviewed / data.total) * 100) : 0;

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6" data-testid="kb-risk-review-page">
      <KbStyleInjector />
      <Link to="/kb/callout-audit" className="inline-flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 dark:text-slate-400 dark:hover:text-slate-100">
        <ArrowLeft className="h-3.5 w-3.5" /> Callout audit
      </Link>
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-slate-100 dark:text-slate-50">
          <ShieldAlert className="h-6 w-6" /> Risk review
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Warning boxes whose wording suggests a real danger, ten at a time. Promote the ones you
          agree with — "Keep as Warning" is remembered and never asked again.
        </p>
      </div>

      <Card className="p-4" data-testid="kb-risk-progress">
        <div className="flex flex-wrap items-baseline gap-3 text-sm">
          <span className="font-semibold text-slate-800 dark:text-slate-100">
            {data ? `${data.reviewed} of ${data.total} reviewed` : "…"}
          </span>
          <span className="text-slate-500 dark:text-slate-400">
            {data ? `${data.promoted} promoted · ${data.kept} kept as warning · ${data.pending} left` : ""}
          </span>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400 dark:text-slate-500" />}
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      </Card>

      {done && (
        <Card className="p-8 text-center" data-testid="kb-risk-done">
          <Check className="mx-auto h-8 w-8 text-emerald-600" />
          <p className="mt-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
            Every flagged box has been reviewed.
          </p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {data.promoted} promoted to Danger, {data.kept} left as Warning.
          </p>
        </Card>
      )}

      {(data?.batch || []).map((row) => (
        <Card key={row.id} className="p-4" data-testid={`kb-risk-card-${row.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">
              <span className={`h-2.5 w-2.5 rounded-full kb-swatch-${row.variant}`} />
              {row.label}
            </span>
            <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
              <ShieldAlert className="h-3 w-3" /> {row.risk.join(", ")}
            </span>
            {row.title && <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{row.title}</span>}
            <Link to={`/kb/a/${row.slug}`} target="_blank" rel="noreferrer"
              className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-primary"
              data-testid={`kb-risk-open-${row.id}`}>
              <ExternalLink className="h-3.5 w-3.5" /> {row.article}
            </Link>
          </div>
          <p className="mt-2 line-clamp-6 text-sm text-slate-600 dark:text-slate-300">{row.text}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" disabled={busy === row.id} onClick={() => decide(row, "promote")}
              data-testid={`kb-risk-promote-${row.id}`}
              className="bg-rose-600 text-white hover:bg-rose-700">
              {busy === row.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Make it Danger
            </Button>
            {row.imported && (
              <Button size="sm" variant="outline" disabled={busy === row.id}
                onClick={() => decide(row, "modernise")} data-testid={`kb-risk-modernise-${row.id}`}
                title="Promote AND rebuild it as a house callout">
                <Sparkles className="mr-1 h-3.5 w-3.5" /> Danger + modernise
              </Button>
            )}
            <Button size="sm" variant="ghost" disabled={busy === row.id}
              onClick={() => decide(row, "keep")} data-testid={`kb-risk-keep-${row.id}`}>
              Keep as Warning
            </Button>
            <span className="text-[11px] text-slate-400 dark:text-slate-500">{row.category}{row.draft ? " · draft" : ""}</span>
          </div>
        </Card>
      ))}
    </div>
  );
}
